import { type PayloadAction, createSlice } from '@reduxjs/toolkit'

import { getCustomerAssetsByAuth0Tenant } from '~/auth/assets'

import type { RootState } from '../index'
import { dispatcherFactory, selectorHookFactory } from '../utils'

/**
 * ⚠️  CRITICAL: PERSISTED SLICE - READ BEFORE MAKING ANY CHANGES ⚠️
 *
 * This Redux slice is PERSISTED TO LocalStorage. Existing users have this state
 * stored in their browsers and it will be automatically loaded on page load.
 *
 * 🚨 BREAKING CHANGE RISKS:
 * - Adding non-optional fields to this slice will BREAK existing users when their
 *   persisted state is loaded from LocalStorage
 * - Renaming existing fields will cause runtime errors for existing users
 * - Changing field types may cause type mismatches and crashes
 *
 * 📋 SAFE CHANGE PRACTICES:
 * ✅ Adding optional fields with default values (configuring a default in `initialState` DOES NOT WORK)
 * ✅ Adding new optional properties to existing objects
 * ✅ Adding new reducer actions (these don't affect persisted state structure)
 * ✅ Modifying selector logic that handles missing fields gracefully
 *
 * 🚫 UNSAFE PRACTICES:
 * ❌ Adding required fields without migration strategy
 * ❌ Renaming or removing existing fields
 * ❌ Changing field types (string -> number, etc.)
 * ❌ Restructuring nested objects without backwards compatibility
 *
 * 💡 IF YOU MUST MAKE BREAKING CHANGES:
 * 1. Implement a migration strategy in the persistence layer
 * 2. Consider versioning the persisted state schema
 * 3. Test thoroughly with existing persisted state scenarios
 * 4. Plan a rollout strategy that handles users with old state
 *
 * Remember: This affects REAL USERS in production who may have months/years
 * of persisted state. One careless change can break the app for thousands of users.
 */

export type TenantInfo = {
  tenant?: string
  email: string
  /** The Auth0 connection an SSO button picked (e.g. `Uber`); absent for the email path. */
  connection?: string
}

type TenantState = {
  tenantInfo: TenantInfo | null
}

const initialState: TenantState = {
  tenantInfo: null
}

const tenantSlice = createSlice({
  name: 'tenant',
  initialState,
  reducers: {
    setTenantInfo: (state, action: PayloadAction<TenantInfo>) => {
      state.tenantInfo = action.payload
    },
    clearTenantInfo: (state) => {
      state.tenantInfo = null
    }
  }
})

export const { setTenantInfo, clearTenantInfo } = tenantSlice.actions
export default tenantSlice.reducer

const selectTenantInfo = (state: RootState) => state.tenant.tenantInfo
export const useTenantInfo = selectorHookFactory(selectTenantInfo)
export const useSetTenantInfo = dispatcherFactory(setTenantInfo)
export const useClearTenantInfo = dispatcherFactory(clearTenantInfo)

export function useCustomerAssetsByTenant() {
  const tenantInfo = useTenantInfo()
  if (!tenantInfo?.tenant) return null
  return getCustomerAssetsByAuth0Tenant(tenantInfo.tenant)
}
